import * as FullStory from '@fullstory/browser';
import { setConfig as setApiConfig } from 'js-api-client';
import Cookies from 'js-cookie';
import coreApi from 'js-api-client/core';
import purchaseApi from '@reservamos/js-api-client/purchase';
import { browserName, deviceType, osName } from 'react-device-detect';
import OneSignal from 'react-onesignal';
import {
  analyticsReady,
  getDistinctId,
  load as loadSegment,
  trackEvent,
  identifyUser,
} from 'user-analytics';
import initMoment from 'utils/initMoment';
import { CurrentLang } from 'utils/lang';
import { getProductType } from 'utils/Reserbus';
import getMixpanelUserAgentProps from 'utils/userAgentProps';
import { isNativeAppWebView, isNativeIOSWebView, isNativeAndroidWebView } from 'utils/webview';
import reactNativeMessage from 'hooks/useReactNativeMessage';
import adyen from '../payments/core/engines/adyen';
import cybersource from '../payments/core/engines/cybersource';
import { loadSearchWidgetScript } from './utils/searchWidget';
import kushki from '../payments/core/engines/kushki';
import growthBook from './services/growthBook';
import store from './store';
import loadBotlers from './utils/botlers';
import loadRiskified from './utils/loadRiskified';
import redirectHttps from './utils/redirectHttps';
import { getSessionId, setInitialSessionId } from './utils/session';
import { initTaplytics } from './utils/taplytics';
import fingerprintService from './services/userFingerprint/userFingerprint';
import reserhubApi from './services/reserhub-api/reserhubApi';
import cieloEngine from '../payments/core/engines/cielo';
import redeEngine from '../payments/core/engines/rede';
import stripeEngine from '../payments/core/engines/stripe';
import isReserhubUnifiedAccountEnabled from './utils/isReserhubUnifiedAccountEnabled';
import {
  getRnMixpanelIdentifyTraits,
  getRnMixpanelNativeProps,
  getRnMixpanelPendingDistinctId,
} from './utils/rnMixpanelNativeProps';

// Used to distinguish between mobile and desktop client
const isDesktop = getProductType() === 'desktop';

/**
 * Initialize all the services needed using the tenant config
 * @param {Object} whitelabelConfig - tenant config
 * @returns {Promise} - Promise that resolves when all the services are initialized
 */
async function initializeClientServices(whitelabelConfig) {
  if (!window) return;

  const { env: whitelabelEnvs, features: whitelabelFeatures } = whitelabelConfig;

  // Load search widget style
  if (whitelabelEnvs.searchWidget.useDynamicLoad)
    loadSearchWidgetScript(
      whitelabelEnvs.searchWidget.version,
      whitelabelEnvs.searchWidget.useDynamicLoad,
    );

  redirectHttps();

  const storeInstance = store.createInstance({ whitelabelConfig });

  // moment
  initMoment(whitelabelEnvs.lang.default, whitelabelFeatures.HOUR_FORMAT);

  // session id used for antifraud beacons
  setInitialSessionId();

  const defaultLanguage = whitelabelEnvs.lang.default;
  const i18nextLng =
    typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem('i18nextLng') || defaultLanguage
      : defaultLanguage;

  if (!whitelabelEnvs.api) throw new Error('whitelabelEnvs.api is undefined');

  let apiKey;
  let productType;

  /**
   * Set the API key and product type based on the device platform.
   * All native-app branches require the authoritative native_app_* UA identifier.
   * @deprecated Use `iOSKey` and `androidKey` instead of `webviewKey`, generic webview detection will be removed in the near future.
   */
  if (isNativeAppWebView() && whitelabelEnvs.api?.webviewKey) {
    apiKey = whitelabelEnvs.api.webviewKey;
    productType = 'webview';
  } else if (isNativeIOSWebView() && whitelabelEnvs.api?.iosKey) {
    apiKey = whitelabelEnvs.api.iosKey;
    productType = 'ios';
  } else if (isNativeAndroidWebView() && whitelabelEnvs.api?.androidKey) {
    apiKey = whitelabelEnvs.api.androidKey;
    productType = 'android';
  } else if (isDesktop && whitelabelEnvs.api?.desktopKey) {
    apiKey = whitelabelEnvs.api.desktopKey;
    productType = 'web';
  } else {
    apiKey = whitelabelEnvs.api.mobileKey;
    productType = 'web-mobile';
  }

  const reserhubUnifiedAccountEnabled = isReserhubUnifiedAccountEnabled(
    whitelabelEnvs.reserhub?.account,
    {
      // GrowthBook may not be initialized yet; `true` avoids blocking progressiveRollout accounts at boot
      unifiedAccountFeature: true,
    },
  );

  const reserhubAccountHasProgressiveRollout = whitelabelEnvs.reserhub?.account?.progressiveRollout;
  const useClassicToken = !(reserhubUnifiedAccountEnabled && !reserhubAccountHasProgressiveRollout);

  // Reservamos API Client
  setApiConfig({
    searchUrl: whitelabelEnvs.api.searchUrl,
    purchaseUrl: whitelabelEnvs.api.purchaseUrl,
    apiKey,
    userAuthToken: useClassicToken ? Cookies.get('token') : undefined,
    headers: {
      'accept-language': i18nextLng,
    },
    withCredentials: reserhubUnifiedAccountEnabled,
  });
  purchaseApi.setConfig({
    baseUrl: whitelabelEnvs.api.purchaseUrl,
    defaultHeaders: {
      Origin: origin,
      Authorization: `Token token=${apiKey}`,
    },
  });
  coreApi.setConfig({
    baseUrl: whitelabelEnvs.coreApi?.url,
    defaultHeaders: {
      Origin: origin,
      Authorization: `Bearer ${apiKey}`,
    },
  });

  // Initialize Reserhub API
  reserhubApi.initialize();

  // conekta
  if (window.Conekta) {
    window.Conekta.setPublicKey(whitelabelEnvs.conekta.pubKey);
  }

  // GrowthBook
  const { brand, growthBook: growthBookConfig } = whitelabelEnvs;

  const browser = browserName.toLowerCase();
  const device = deviceType.toLowerCase();
  const os = osName.toLowerCase();

  if (growthBookConfig?.enabled) {
    growthBook.init({
      enableDevMode: growthBookConfig.enableDevMode,
      trackingCallback: (experiment, result) => {
        // Send the experiment data to mixpanel
        trackEvent('$experiment_started', {
          'Experiment name': experiment.key,
          'Variant value': result.value,
          '$source': 'growthbook',
        });
      },
    });
    growthBook.setAttributes({
      brand,
      browser,
      device,
      os,
      product: productType,
    });
    growthBook.loadFeatures(growthBookConfig.featuresEndpoint);
  }

  // User Fingerprint
  const { userFingerprint } = whitelabelEnvs;

  if (userFingerprint?.enabled) {
    await fingerprintService.init(userFingerprint);
    fingerprintService.setUserMetadata({
      tag: {
        tenant: brand,
        productType,
      },
    });
  }

  /**
   * Returns the super props to sent in all the mixpanel events.
   * When running inside a native app WebView, saas-app injects super props before content
   * loads and registers them via applyRnMixpanelBridge when Segment loads.
   * @returns {Object} superProps
   */
  const getMixpanelSuperProps = () => {
    const superProps = { product: productType };

    if (whitelabelFeatures.REGISTER_USER_AGENT_PROPS_INTO_MIXPANEL) {
      return getMixpanelUserAgentProps(superProps);
    }

    return superProps;
  };

  const mixpanelSuperProps = getMixpanelSuperProps();

  /**
   * Register fingerprint into the Mixpanel superProps
   */
  const syncFingerprintAndMixpanel = async ({ distinctId }) => {
    // The fingerprint is gotten only if we are in the initial pages to save resources
    const pathValidForGetFingerprint =
      window.location.pathname.includes('/search') || window.location.pathname === '/';

    // Adding device fingerprint to mixpanel super pros and distinct id to user fingerprint
    fingerprintService.setUserMetadata({
      linkedId: distinctId,
    });

    let userFingerprintData;
    if (pathValidForGetFingerprint) {
      userFingerprintData = await fingerprintService.setUpFingerprint();
    } else {
      userFingerprintData = await fingerprintService.getCachedFingerprint();
    }
    if (userFingerprintData)
      window.mixpanel.register({
        [fingerprintService.getTrackingKey()]: userFingerprintData?.visitorId,
        'Visitor Found': userFingerprintData?.visitorFound,
      });
  };

  /**
   * Synchronizes the external user ID with Mixpanel and returns the user ID
   */
  function syncExternalUserId() {
    const queryString = new URLSearchParams(window.location.search);
    const userId = queryString.get('userId');

    if (userId) {
      identifyUser(userId, { 'User Type': 'Anonymous' });
    }

    return userId || getDistinctId();
  }

  /**
   * Applies native Mixpanel super props and distinct id bridged from saas-app (early injection
   * or postMessage). Search / View Results can fire before loadEnd; early globals + explicit
   * merge in search trackers cover that window.
   */
  const applyRnMixpanelBridge = ({ distinctId, props } = {}) => {
    const bridgedProps = props || getRnMixpanelNativeProps();
    if (bridgedProps && typeof bridgedProps === 'object' && Object.keys(bridgedProps).length) {
      // eslint-disable-next-line no-underscore-dangle
      window.__RN_MIXPANEL_NATIVE_PROPS__ = bridgedProps;
      if (window.mixpanel?.register) {
        window.mixpanel.register(bridgedProps);
      }
    }

    const bridgedDistinctId = distinctId || getRnMixpanelPendingDistinctId() || null;
    if (bridgedDistinctId) {
      identifyUser(bridgedDistinctId, getRnMixpanelIdentifyTraits());
      // eslint-disable-next-line no-underscore-dangle
      window.__RN_MIXPANEL_IDENTIFIED_AS__ = bridgedDistinctId;
    }
  };

  /**
   * Function executed when the tracking engine is loaded
   */
  const onLoadedSegment = () => {
    if (isNativeAppWebView()) {
      applyRnMixpanelBridge();
    }

    const distinctId = syncExternalUserId();
    syncFingerprintAndMixpanel({ distinctId });

    if (growthBookConfig?.enabled) {
      growthBook.setAttributes({ id: distinctId, isEligibleForQuickPurchase: false });
    }

    // This is done to make know another libraries that mixpanel is ready to be used
    window.dispatchEvent(new CustomEvent('MixpanelReady'));
  };

  // segment
  loadSegment(whitelabelEnvs.segment.token, {
    ...whitelabelEnvs.segment.options,
    superProps: mixpanelSuperProps,
    onLoaded: onLoadedSegment,
  });

  // saas-app injects __RN_MIXPANEL_NATIVE_PROPS__ before content loads and posts
  // setMpDistinctId after loadEnd (refresh $browser, re-identify).
  if (isNativeAppWebView()) {
    analyticsReady(() => {
      const RNMessage = reactNativeMessage();
      RNMessage.listen(({ data }) => {
        const { action, message, props } = data || {};
        if (action !== 'setMpDistinctId') return;
        applyRnMixpanelBridge({ distinctId: message, props });
      });
    });
  }

  if (whitelabelEnvs.fullstory?.enabled) {
    FullStory.init({
      orgId: whitelabelEnvs.fullstory.orgId,
      debug: !__PROD__,
    });
  }

  // cybersource
  if (whitelabelEnvs.cybersource?.enabled) {
    const { orgId, merchantId, isSandbox, clientLibrary } = whitelabelEnvs.cybersource;
    cybersource.loadCybersourceFlex(clientLibrary)();
    if (!whitelabelFeatures.DYNAMIC_CYBERSOURCE_CREDENTIALS) {
      cybersource.initialize({
        orgId,
        merchantId,
        isSandbox,
        sessionId: getSessionId(),
      });
    }
  }

  // riskified
  loadRiskified(whitelabelEnvs.riskified);

  if (whitelabelEnvs.botlers && whitelabelEnvs.botlers.enabled) {
    loadBotlers(whitelabelEnvs.botlers.bmid);
  }

  if (whitelabelEnvs.kushki && whitelabelEnvs.kushki.enabled) {
    const { merchantId, isSandbox } = whitelabelEnvs.kushki;
    kushki.initialize({ merchantId, inTestEnvironment: isSandbox });
  }

  if (whitelabelEnvs.adyen && whitelabelEnvs.adyen.enabled) {
    if (
      whitelabelEnvs.adyen.creditCard?.enabled ||
      whitelabelEnvs.adyen.applePay?.enabled ||
      whitelabelEnvs.adyen.googlePay?.enabled
    ) {
      adyen.initialize({
        ...whitelabelEnvs,
        lang: { current: CurrentLang(), default: whitelabelEnvs.lang.default },
      });
    }
  }

  if (whitelabelEnvs.cielo?.enabled) {
    const { tokenizeEndpoint, threeDSTokenEndpoint, sopAccessTokenEndpoint } = whitelabelEnvs.cielo;
    cieloEngine.init({ tokenizeEndpoint, threeDSTokenEndpoint, sopAccessTokenEndpoint });
  }

  if (whitelabelEnvs.rede?.enabled) {
    const { tokenizeEndpoint } = whitelabelEnvs.rede;
    redeEngine.init({ tokenizeEndpoint });
  }

  if (whitelabelEnvs.stripe?.enabled) {
    stripeEngine.initialize({
      ...whitelabelEnvs,
      locale: CurrentLang(),
    });
  }

  // One Signal — production apps are domain-restricted; skip on local funnel hosts.
  const { hostname } = window.location;
  const isLocalFunnelHost =
    hostname === 'localhost' ||
    hostname.endsWith('.platform.localhost') ||
    hostname.endsWith('.platform.test');

  if (whitelabelEnvs.oneSignal.enabled && !isLocalFunnelHost) {
    OneSignal.init({
      appId: whitelabelEnvs.oneSignal.appId,
      notifyButton: {
        enable: true,
      },
    });
  }

  // Taplytics
  initTaplytics(whitelabelEnvs.taplytics);

  return { storeInstance };
}

export default initializeClientServices;
